import { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const rd = (v) => `RD$ ${Number(v || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const box = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3333]/30";

export const Field = ({ label, children }) => <label className="block space-y-1 min-w-0"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;

export function Input({ row, k, type = "text", onSave, tid, cls = "" }) {
  const cur = row[k];
  const [v, setV] = useState(cur ?? "");
  useEffect(() => { setV(cur ?? ""); }, [cur]);
  return <input type={type} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => String(v) !== String(cur ?? "") && onSave({ [k]: v })} className={`${box} ${cls}`} data-testid={tid} />;
}

export const Select = ({ row, k, opts, onSave, tid, cls = "" }) => (
  <select value={row[k] ?? opts[0][0]} onChange={(e) => onSave({ [k]: e.target.value })} className={`${box} ${cls}`} data-testid={tid}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const Txt = ({ row, k, label, type, onSave, prefix }) => <Field label={label}><Input row={row} k={k} type={type} onSave={onSave} tid={`${prefix}-${k}-${row.id}`} /></Field>;
export const Pick = ({ row, k, label, opts, onSave, prefix }) => <Field label={label}><Select row={row} k={k} opts={opts} onSave={onSave} tid={`${prefix}-${k}-${row.id}`} /></Field>;

export const Stat = ({ l, v, tid, tone = "" }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={tid}>
    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{l}</p><p className={`font-mono-num text-lg sm:text-2xl mt-2 ${tone}`}>{v}</p>
  </div>
);

export function useColl(coll, params) {
  const [rows, setRows] = useState([]);
  const key = JSON.stringify(params || {});
  const chain = useRef(Promise.resolve());
  const pending = useRef(0);
  const load = () => api.get(`/admin/c/${coll}`, { params: params || {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [coll, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async (doc) => {
    try { const r = await api.post(`/admin/c/${coll}`, doc); setRows((x) => [r.data, ...x]); return r.data; } catch (e) { toast.error(errMsg(e)); }
  };
  const save = (id, patch) => {
    setRows((x) => x.map((y) => (y.id === id ? { ...y, ...patch } : y)));
    pending.current += 1;
    chain.current = chain.current.then(async () => {
      try { const r = await api.put(`/admin/c/${coll}/${id}`, patch); if (pending.current === 1) setRows((x) => x.map((y) => (y.id === id ? r.data : y))); return r.data; } catch (e) { toast.error(errMsg(e)); } finally { pending.current -= 1; }
    });
    return chain.current;
  };
  const del = async (id, what = "este registro") => {
    if (!window.confirm(`¿Eliminar ${what}?`)) return;
    try { await api.delete(`/admin/c/${coll}/${id}`); setRows((x) => x.filter((y) => y.id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };
  return { rows, setRows, load, add, save, del };
}

export function InlineTable({ prefix, cols, rows, save, del, addLabel, onAdd, empty, footer }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid={`${prefix}-table`}>
      <div className="flex justify-end p-3"><button onClick={onAdd} className="h-10 rounded-full bg-[#FF3333] hover:bg-[#E62E2E] text-white px-5 text-sm font-semibold inline-flex items-center gap-2" data-testid={`${prefix}-add-btn`}><Plus size={15} />{addLabel}</button></div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left"><tr>{cols.map((c) => <th key={c.k} className="px-2 py-3 whitespace-nowrap">{c.label}</th>)}<th /></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`${prefix}-row-${r.id}`}>
              {cols.map((c) => (
                <td key={c.k} className="px-1.5 py-2" style={{ minWidth: c.w || 110 }}>
                  {c.render ? c.render(r) : c.opts ? <Select row={r} k={c.k} opts={c.opts} onSave={(p) => save(r.id, p)} tid={`${prefix}-${c.k}-${r.id}`} />
                    : <Input row={r} k={c.k} type={c.type} onSave={(p) => save(r.id, p)} tid={`${prefix}-${c.k}-${r.id}`} />}
                </td>
              ))}
              <td className="px-1.5 py-2"><button onClick={() => del(r.id)} className="p-2.5 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`${prefix}-delete-${r.id}`}><Trash2 size={16} /></button></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={cols.length + 1} className="text-center text-slate-400 py-10" data-testid={`${prefix}-empty`}>{empty}</td></tr>}
        </tbody>
        {footer}
      </table>
    </div>
  );
}
